import { Injectable } from '@angular/core';
import { RouteSignposts, Road, RouteLeg, RequestModel, RouteSignpostList, MemberBenefits } from '../../models/route-signposts-model';
import { SummaryData } from '../../models/route-summary-model';
import { Observable, forkJoin, Observer } from 'rxjs';
import { RoutePlannerService } from '../route-planner/route-planner.service';
import { AALocationModel } from '../../models/route-model';
import { GoogleMapHTTPService } from 'src/app/modules/aa-core/services/google/google-map-http/google-map-http.service';
import { RouteDataStoreService } from 'src/app/modules/aa-core/services/data-store/data-store.service';
import { GoogleMapStoreService } from 'src/app/modules/google-map/services/google-map-store/google-map-store.service';
import { IGoogleMap } from 'src/app/core/models/google-map-models';
import { GoogleMapType } from 'src/app/modules/google-map/enums/google-map-enum';
import { ServiceStationMarkerService } from 'src/app/modules/google-map/services/marker-cluster/service-station-marker.service';
import { BENEFIT_OFFERS_TYPE } from '../../enums/route.enum';

@Injectable({
  providedIn: 'root'
})
export class RouteSignpostsService {

 public routeSignpostList: RouteSignpostList;
 public prefixCount;
 public suffixCount;
  constructor(
    private readonly googleMapHttp: GoogleMapHTTPService,
    private readonly dataStore: RouteDataStoreService,
    private readonly googleMapStore: GoogleMapStoreService,
    private readonly serviceStationMarkerService: ServiceStationMarkerService,
    private readonly routePlannerService: RoutePlannerService) {
      this.routeSignpostList = this.dataStore.routeDataModel.RouteSignpostList;
    }

  public updateSignSpots = (selectedRoute: SummaryData) => {
    this.routeSignpostList.reset();

    const routeOrder: Array<AALocationModel> = this.routePlannerService.getRouteOrder();

    selectedRoute.legs.forEach( (leg, index) => {
      this.routeSignpostList.addRoute( this.createRouteSignpost(leg, index, routeOrder) );
    });

    this.routeSignpostList.setDistanceType(selectedRoute.DistanceType);
    this.routeSignpostList.updateDistance();
    this.routeSignpostList.updateDuration();

    // load service stations
    this.getServiceStation(selectedRoute);

    return new Observable( observer => {
      forkJoin( [this.getRouteSignpost()] ).subscribe( (results: Array<any>) => {
        observer.next(this.routeSignpostList);
      });
    });
  }

  private createRouteSignpost(selectedRoute, index, routeOrder: Array<AALocationModel>): RouteSignposts {
    const routeSignpost = new RouteSignposts('routeSignpost' + index);
    const startLocation: AALocationModel = routeOrder[index];
    const endLocation: AALocationModel = routeOrder[index + 1];

    if (startLocation) {
      const startRoute: RouteLeg = new RouteLeg();
      startRoute.Summary = startLocation.Name;
      startRoute.location = {lat: selectedRoute.start_location.lat(), lng: selectedRoute.start_location.lng()};
      routeSignpost.StartRouteLeg = startRoute;
    }

    let previousRouteLeg: RouteLeg;
    selectedRoute.steps.forEach(element => {
      previousRouteLeg = routeSignpost.addRouteDirection( element, previousRouteLeg );
    });

    if (endLocation) {
      const endRoute: RouteLeg = new RouteLeg();
      endRoute.Summary = endLocation.Name;
      endRoute.location = {lat: selectedRoute.end_location.lat(), lng: selectedRoute.end_location.lng()};
      endRoute.distanceValue = previousRouteLeg.distanceValue;
      routeSignpost.EndRouteLeg = endRoute;
    }

    const duration = selectedRoute.duration_in_traffic || selectedRoute.duration;
    if (duration) {
      routeSignpost.SectionDuration = duration.value;
    }

    return routeSignpost;
  }

  private getSignpostPayloadData(): RequestModel.SignpostsRequestModel {
    const payload: RequestModel.SignpostsRequestModel = new RequestModel.SignpostsRequestModel();
    payload.distanceKm = 0.010;
    payload.namedPoints = [];

    this.routeSignpostList.RouteSignpost.forEach( routeSignpost => {
      routeSignpost.RouteLegs.forEach( (data: RouteLeg) => {
        const location = data.leg.start_location;
        const requestModel = new RequestModel.RoadRequestModel(data.Name, location.lat(), location.lng(), data.headingDegrees);
        requestModel.setHints( data.Summary );
        payload.namedPoints.push(requestModel);
      });
    });

    return payload;
  }

  private getRouteSignpost(): Observable<any> {
    const payload = this.getSignpostPayloadData();
    // console.log('payload', this.routeSignpostList.RouteSignpost, payload);

    return Observable.create( (observer: Observer<any>) => {
      this.googleMapHttp.getRouteSignspots( payload ).subscribe( data => {
        // console.log('Updated Roads---> ', data);
        data.forEach( item => {
          const routeLeg: RouteLeg = this.getRouteLeg(item.queryName);
          if (routeLeg) {
            this.prefixCount = 0;
            this.suffixCount = 0;
            routeLeg.Type = item.hit.type;
            routeLeg.Prefix = item.hit.prefix || '';
            routeLeg.Suffix = item.hit.suffix || '';
            routeLeg.Road = [];
            item.hit.roads.forEach(element => {
              const road: Road = new Road(element.type);
              if (element.name) { road.Name.push( element.name ); }
              if (element.text) { road.Text.push( ...element.text ); }
              if (element.icon) { road.Icon = element.icon; }

              if (routeLeg.Prefix !== '' && this.prefixCount === 0) {
                  this.prefixCount ++;
                  road.Prefix.push(routeLeg.Prefix || '');
              }

              if (routeLeg.Suffix !== '' && this.suffixCount === 0) {
                 this.suffixCount ++;
                 road.Suffix.push(routeLeg.Suffix || '');
              }
             routeLeg.Road.push(road);
            });
          }
        });

        observer.next(data);
        observer.complete();
      });
    });
  }

  private getRouteLeg(forThisName: string): RouteLeg {
    let routeLeg: RouteLeg;

    for (let index = 0; index < this.routeSignpostList.RouteSignpost.length; index++) {
      const routeSignpost = this.routeSignpostList.RouteSignpost[index];

      routeLeg = routeSignpost.RouteLegs.filter( routeLegItem => {
        return routeLegItem.Name === forThisName;
      })[0];

      if (routeLeg) {
        break;
      }
    }
    return routeLeg;
  }

  public isOfferPartnerMB = (offerPartner: string) => offerPartner === BENEFIT_OFFERS_TYPE.MB;

  // member befefit
  private getMemberBenefits(): Observable<any> {
    const payload = this.getRequestDataModel();

    return Observable.create( (observer: Observer<any>) => {
      this.googleMapHttp.getMemberBenefits( payload ).subscribe( data => {

        // remove duplicate member benefits by hit.rewardSiteId;
        const filteredData: Object = {};
        data.forEach( item => {
          filteredData[item.hit.rewardSiteId] = item;
        });

        for (const key in filteredData) {
          const item = filteredData[key];
          const routeLeg: RouteLeg = this.getRouteLeg(item.queryName);
          // Switch off M&B MemberBenefits offers
          if (routeLeg && !this.isOfferPartnerMB(item.hit.partner)) {
            routeLeg.MemberBenefits = Object.assign( new MemberBenefits(), item.hit);
          }
        }

        observer.next(data);
        observer.complete();
      });
    });
  }

  private getRequestDataModel(): RequestModel.MemberBenefitModel {
    const requestDataModel: RequestModel.MemberBenefitModel = new RequestModel.MemberBenefitModel();
    requestDataModel.distanceKm = 0.100;
    requestDataModel.namedPoints = [];

    this.routeSignpostList.RouteSignpost.forEach( routeSignpost => {
      routeSignpost.RouteLegs.forEach( (data: RouteLeg) => {
        requestDataModel.namedPoints.push(
          new RequestModel.MemberBenefitItemModel(data.Name, data.leg.end_location.lat(), data.leg.end_location.lng()) );
      });
    });

    return requestDataModel;
  }

  private getServiceStation(selectedRoute: SummaryData): any {
    const googleMap: IGoogleMap = this.googleMapStore.getGoogleMap(GoogleMapType.ROUTE_PLANNER);
    this.serviceStationMarkerService.loadTravelData(googleMap, selectedRoute);
  }
}
